import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { find } from "lodash";
import { useLocalStorage } from "@/react/hooks/utils/useLocalStorage";
import { LOCALSTORAGE_KEYS } from "@circle-react/config/Settings";
import { fetchRequest } from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";
import type { SpaceGroup } from "../types";
import type { LoadingStatus } from "./loadingStatuses";
import { ERROR, LOADED, LOADING, REFRESHING } from "./loadingStatuses";

// In long run we may have multiple states for status: loading, reloading, etc.

interface SpaceGroupContextStateType {
  actions: {
    loadData: () => Promise<void>;
    refresh: () => void;
  };
  helpers: {
    isDataLoading: () => boolean;
    isDataRefreshing: () => boolean;
    isDataLoaded: () => boolean;
    findById: any;
    findBy: any;
  };
  isLoading: boolean;
  records: SpaceGroup[];
  status: LoadingStatus;
}

const initialState: SpaceGroupContextStateType = {
  status: LOADING,
  records: [],
  actions: {
    loadData: () => Promise.resolve(),
    refresh: () => {},
  },
  helpers: {
    isDataLoading: () => false,
    isDataRefreshing: () => false,
    isDataLoaded: () => false,
    findById: () => undefined,
    findBy: () => undefined,
  },
  isLoading: false,
};

const SpaceGroupsContext =
  createContext<SpaceGroupContextStateType>(initialState);
SpaceGroupsContext.displayName = "SpaceGroupsContext";

export interface SpaceGroupsContextProviderProps {
  children: ReactNode;
  spaceGroups?: SpaceGroup[];
}

export const SpaceGroupsContextProvider = ({
  children,
  spaceGroups,
}: SpaceGroupsContextProviderProps) => {
  const [status, setStatus] = useState(LOADING);
  const [records, setRecords] = useState<SpaceGroup[]>([]);
  const { getValue: getLocalStorageValue, setValue: setLocalStorageValue } =
    useLocalStorage<any>(LOCALSTORAGE_KEYS.SpaceGroupsContextProvider);

  const onDataLoadSuccess = (data: any) => {
    setRecords(data.records);
    setStatus(LOADED);
    setLocalStorageValue(data.records);
  };

  useEffect(() => {
    if (spaceGroups) {
      setRecords(spaceGroups);
      setStatus(LOADED);
    } else {
      const localStorageSpaceGroups = getLocalStorageValue();
      if (localStorageSpaceGroups && localStorageSpaceGroups.length > 0) {
        setRecords(localStorageSpaceGroups);
        setStatus(LOADED);
      }
      void loadData();
    }
  }, []);

  const loadData = async () => {
    const response = (await fetchRequest(internalApi.spaceGroups.index(), {
      method: "GET",
    })) as Response;

    if (response.ok) {
      onDataLoadSuccess(await response.json());
    } else {
      setStatus(ERROR);
    }
  };

  const refresh = () => {
    setStatus(REFRESHING);
    void loadData();
  };

  const isDataLoaded = () => status === LOADED;
  const isDataLoading = () => status === LOADING;
  const isDataRefreshing = () => status === REFRESHING;

  const findById = (id: string) => findBy({ id });

  // uses: https://lodash.com/docs/4.17.15#find
  const findBy = (conditions = {}) => {
    if (!isDataLoaded()) {
      console.warn(`findBy called when status = ${status}.`);
    }
    return find(records, conditions);
  };

  const value: SpaceGroupContextStateType = {
    status,
    records,
    actions: { loadData, refresh },
    isLoading: isDataLoading(),
    helpers: {
      isDataLoading,
      isDataRefreshing,
      isDataLoaded,
      findById,
      findBy,
    },
  };
  return (
    <SpaceGroupsContext.Provider value={value}>
      {children}
    </SpaceGroupsContext.Provider>
  );
};

export const useSpaceGroupsContext = (): SpaceGroupContextStateType =>
  useContext(SpaceGroupsContext);
